import { useCallback, useMemo } from 'react';
import type { API_RESPONSE } from '@corefront/eva-fe-sdk';
import { useSelector } from 'react-redux';
import { useGetSidebarLimit } from '../../hooks';
import { useQueryCategories } from '../../../../hooks/API/useQueryCategories';
import {
  useEntityIconPathCallback,
  useRedirectToDetailsPageCallbackWithAnalyticsData,
} from '../hooks';
import { useAnalyticsScreenName } from '../../../../hooks/useAnalyticsScreenName';
import {
  categoryLogoImageSelector,
  sidebarMenuItemLimitsSelector,
} from '../../../../redux/reducers/app/configuration';
import { getCategoryImagePath } from '../../../../utils/imagePath';
import { getCategoryDetailRoute } from '../../../../utils/routes';
import type { NavigationItem } from '../types';
import { NAMESPACES } from '../../../../constants/namespaces';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREA_DETAILS,
  ANALYTICS_SCREEN_AREAS,
} from '../../../../constants/analytics';

const useCategoryIconPathCallback = () => {
  return useEntityIconPathCallback(
    categoryLogoImageSelector,
    getCategoryImagePath
  );
};

const ANALYTICS_DATA_CATEGORY = {
  schema: ANALYTICS_SCHEMA_URLS.MORE_GAME_CLICK,
  screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.CATEGORY,
};

const useTransformCategoriesToNavigationItemCallback = () => {
  const iconPathBuilder = useCategoryIconPathCallback();
  const redirectToCategoryDetailsPageWithAnalytics =
    useRedirectToDetailsPageCallbackWithAnalyticsData(getCategoryDetailRoute);

  const screenName = useAnalyticsScreenName();

  return useCallback(
    (categories: API_RESPONSE.Categories['200']['items']): NavigationItem[] => {
      return (
        categories?.map(({ id, translationKey, gameCount }) => {
          return {
            id,
            translationKey,
            onItemClick: redirectToCategoryDetailsPageWithAnalytics,
            iconPathBuilder,
            linkPathBuilder: getCategoryDetailRoute,
            rightCellContent: gameCount?.toString(),
            namespace: NAMESPACES.CATEGORY,
            analyticsData: {
              ...ANALYTICS_DATA_CATEGORY,
              screen_name: screenName,
              event_detail: id,
              screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
            },
          };
        }) || []
      );
    },
    [redirectToCategoryDetailsPageWithAnalytics, iconPathBuilder, screenName]
  );
};

export const useCategoriesNavigation = () => {
  const { categories: categoriesLimit } = useSelector(
    sidebarMenuItemLimitsSelector
  );
  const limit = useGetSidebarLimit(categoriesLimit);
  const isQueryEnabled = limit !== 0;

  const transformer = useTransformCategoriesToNavigationItemCallback();

  const { data, isLoading, isSuccess } = useQueryCategories(
    { _limit: limit },
    { enabled: isQueryEnabled }
  );

  const items = useMemo(
    () =>
      isQueryEnabled && isSuccess && data?.items?.length
        ? transformer(data.items)
        : [],
    [isSuccess, data, isQueryEnabled, transformer]
  );

  return useMemo(
    () => ({
      isLoading,
      items,
    }),
    [isLoading, items]
  );
};
